import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HiBanknotes, HiCheck, HiLink, HiReceiptPercent, HiSquare2Stack, HiUserPlus } from "react-icons/hi2";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { formatCurrencyAmount, normalizeCurrencyCode } from "../utils/currencyUtils";
import {
  DashboardHero,
  ErrorState,
  Meter,
  Panel,
  RangeTabs,
  StatTile,
  TileSkeleton,
  TrendChart,
  bucketLabel,
  bucketTitle,
  formatCount,
  formatCountTick,
  formatMoney,
  formatMoneyTick,
  previousLabel,
  toRows,
  useActivity,
} from "../components/dashboard-kit";

const getAppOrigin = () => (typeof window === "undefined" ? "" : window.location.origin);

const parseDateValue = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatEarningEndDate = (value) => {
  const date = parseDateValue(value);
  return date ? date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : null;
};

const daysUntil = (value) => {
  const date = parseDateValue(value);
  if (!date) return null;
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return Math.ceil((end - new Date()) / 86400000);
};

function AffiliateDashboard() {
  const { userid } = useSelector((state) => state.auth);
  const { profiledata, fetchDataprofile } = useIndexPageData();
  const [range, setRange] = useState("30d");
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [copied, setCopied] = useState(false);
  const activity = useActivity("/affiliate-stats/activity/", range);

  const fetchStats = useCallback(async () => {
    setStatsError("");
    const result = await apiClient.get("/affiliate-stats/");
    if (!result?.ok) {
      setStatsError("Unable to load affiliate dashboard data.");
      return;
    }
    setStats(result.data);
  }, []);

  useEffect(() => {
    fetchStats();
    if (userid) fetchDataprofile();
  }, [fetchStats, userid]);

  const isOnboarded = Boolean(stats?.connect_onboarded);
  const referralLink = userid ? `${getAppOrigin()}?referral_id=${userid}` : "";
  const commissionCurrency = normalizeCurrencyCode(stats?.currency || "USD");
  const balanceCurrency = normalizeCurrencyCode(stats?.balance_currency || stats?.currency || "USD");
  const name = profiledata?.full_name?.trim();
  const earningEndDate =
    stats?.referral_earning_end_date || stats?.referralEarningEndDate || profiledata?.referral_earning_end_date || profiledata?.referralEarningEndDate || "";
  const earningEndDateLabel = formatEarningEndDate(earningEndDate);
  const daysLeft = daysUntil(earningEndDate);
  const earningActive = daysLeft != null && daysLeft >= 0;
  const commissionRate = stats?.commission_percentage == null ? null : Number(stats.commission_percentage);

  const copyReferralLink = async () => {
    if (!isOnboarded || !referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied to clipboard.");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Unable to copy the referral link.");
    }
  };

  const { data, loading, refreshing, error, reload } = activity;
  const money = (v) => formatMoney(v, data?.currency || "USD");
  const rows = useMemo(() => toRows(data, bucketLabel, bucketTitle), [data]);
  const totals = data?.totals || {};
  const previous = data?.previous || {};
  const prev = previousLabel(range);
  const lifetimeReferrals = data?.lifetime_referrals ?? stats?.total_referrals ?? 0;
  const lifetimeBuyers = data?.lifetime_buyers ?? 0;

  return (
    <main className="dk-page dk-stack">
      <DashboardHero
        eyebrow="Affiliate hub"
        name={name}
        subtitle={`Share your link and earn ${commissionRate ?? 0}% of Mockee's commission on every eligible purchase your referrals make.`}
        figureLabel={`Total earned (${commissionCurrency})`}
        figure={stats ? formatCurrencyAmount(stats.total_earned, commissionCurrency) : "—"}
        figureNote={stats ? <>{formatCurrencyAmount(stats.balance, balanceCurrency)} available to pay out</> : null}
      >
        <div className="dk-referral">
          <span className="dk-referral__icon" aria-hidden="true">
            <HiLink />
          </span>
          <code className="dk-referral__url">{referralLink || "Your referral link will appear here."}</code>
          <button type="button" className="dk-btn dk-btn--light" disabled={!isOnboarded || !referralLink} onClick={copyReferralLink}>
            {copied ? <HiCheck aria-hidden="true" /> : <HiSquare2Stack aria-hidden="true" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        {stats && !isOnboarded && (
          <p className="dk-referral__hint">
            Complete Stripe onboarding in <Link to="/affiliate/billing-information">Billing Information</Link> before sharing your link.
          </p>
        )}
      </DashboardHero>

      {statsError && <ErrorState message={statsError} onRetry={fetchStats} />}

      <div className="dk-toolbar dk-rise" style={{ "--i": 1 }}>
        <div>
          <h2 className="dk-toolbar__title">Referral performance</h2>
          <p className="dk-toolbar__sub">Commission is shown in USD as it was credited to you.</p>
        </div>
        <RangeTabs value={range} onChange={setRange} />
      </div>

      {error && !data && <ErrorState message={error} onRetry={reload} />}
      {loading && !data ? (
        <TileSkeleton count={4} />
      ) : (
        data && (
          <div className="dk-tiles">
            <StatTile index={2} label="Commission earned" icon={HiBanknotes} value={totals.commission} format={money} current={totals.commission} previous={previous.commission} previousLabel={prev} trend={data.series.commission} />
            <StatTile index={3} label="New referrals" icon={HiUserPlus} value={totals.referrals} format={formatCount} current={totals.referrals} previous={previous.referrals} previousLabel={prev} trend={data.series.referrals} />
            <StatTile index={4} label="Referred purchases" icon={HiReceiptPercent} value={totals.referred_sales} format={formatCount} current={totals.referred_sales} previous={previous.referred_sales} previousLabel={prev} trend={data.series.referred_sales} />
            <StatTile index={5} label="Commission rate" icon={HiLink} value={commissionRate ?? "—"} format={(v) => `${Math.round(v * 10) / 10}%`} hint="Of Mockee's platform commission" />
          </div>
        )
      )}

      <div className="dk-grid">
        <Panel className="dk-span-7" index={6} title="Commission" subtitle="What your referrals earned you">
          <TrendChart
            rows={rows}
            refreshing={refreshing}
            series={[{ key: "commission", label: "Commission", kind: "area" }]}
            formatValue={money}
            formatTick={(v) => formatMoneyTick(v, data?.currency || "USD")}
            caption="Commission by period"
            emptyText="No commission in this period yet — share your link to get started."
          />
        </Panel>
        <Panel className="dk-span-5" index={7} title="New referrals" subtitle="People who signed up with your link">
          <TrendChart
            rows={rows}
            refreshing={refreshing}
            series={[{ key: "referrals", label: "Sign-ups", kind: "bar" }]}
            formatValue={formatCount}
            formatTick={formatCountTick}
            integer
            caption="Referral sign-ups by period"
            emptyText="No sign-ups in this period."
          />
        </Panel>

        <Panel className="dk-span-6" index={8} title="Referral conversion" subtitle="All time">
          <div className="dk-stack" style={{ gap: 18 }}>
            <Meter
              label="Referrals who bought a mockup"
              valueText={lifetimeReferrals ? `${formatCount(lifetimeBuyers)} of ${formatCount(lifetimeReferrals)} · ${Math.round((lifetimeBuyers / lifetimeReferrals) * 100)}%` : "No referrals yet"}
              ratio={lifetimeReferrals ? lifetimeBuyers / lifetimeReferrals : 0}
              caption="Every purchase they make earns you commission while your earning period is active."
            />
            <div>
              <div className="dk-kv">
                <span>Total referrals</span>
                <strong>{formatCount(lifetimeReferrals)}</strong>
              </div>
              <div className="dk-kv">
                <span>Lifetime commission</span>
                <strong>{stats ? formatCurrencyAmount(stats.total_earned, commissionCurrency) : "—"}</strong>
              </div>
            </div>
          </div>
        </Panel>

        <Panel
          className="dk-span-6"
          index={9}
          title="Earning period"
          subtitle="You earn commission from your referrals until this date."
          action={
            earningEndDateLabel ? (
              <span className={`dk-badge ${earningActive ? "dk-badge--good" : "dk-badge--muted"}`}>{earningActive ? "Active" : "Ended"}</span>
            ) : null
          }
        >
          <div className="dk-kv">
            <span>Earnings until</span>
            <strong>{earningEndDateLabel || "Not set"}</strong>
          </div>
          <div className="dk-kv">
            <span>Time left</span>
            <strong>{daysLeft == null ? "—" : earningActive ? `${formatCount(daysLeft)} day${daysLeft === 1 ? "" : "s"}` : "Ended"}</strong>
          </div>
          <div className="dk-kv">
            <span>Available balance</span>
            <strong>{stats ? formatCurrencyAmount(stats.balance, balanceCurrency) : "—"}</strong>
          </div>
          <div className="dk-kv">
            <span>Pending balance</span>
            <strong>{stats ? formatCurrencyAmount(stats.pending_balance, balanceCurrency) : "—"}</strong>
          </div>
          {earningEndDateLabel && !earningActive && (
            <p className="dk-meter__caption mt-2">This earning period has ended. New referral purchases will not add commission.</p>
          )}
        </Panel>
      </div>
    </main>
  );
}

export default AffiliateDashboard;
